/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "VISITS" TAB                           *
 *  File: apps/web/src/pages/bridge/VisitsTab.js                   *
 *  What it does: the technicians' Zoho meetings on their way to   *
 *  the Dispatch calendar. A strip says how visits reach the       *
 *  calendar (the owner's switch on Dispatch's panel: approve each *
 *  or automatic) and what a tech sees of his day. Then four       *
 *  views: waiting, on the calendar, cancelled in Zoho, set aside. *
 *  "Approve selected" approves several with one reason.           *
 *  Reads: GET /visits/settings, GET /inbox?kind=visit.            *
 *  Writes: approve / approve-many / reject, only on confirm.      *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE VIEWS                                          *
 *  SECTION 2 — THE SWITCH STRIP                                   *
 *  SECTION 3 — THE TAB                                            *
 *****************************************************************/

// WHY (2026-10-13, James): "Yes, build it on those defaults" - approve each for
// a one-week trial, then the owner flips Dispatch's switch to automatic; the
// one-job-at-a-time rule stays an owner switch (shown here, changed there).

import React, { useCallback, useEffect, useState } from 'react';
import VisitCard from './VisitCard';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS } from './bridgeWords';
import { LoadState, WhyBox, ToneChip } from './bridgeParts';
import { fetchVisits, fetchVisitSettings, approveManyRecords } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE VIEWS                                          *
 *****************************************************************/

// Each view: its id, its word, which statuses it asks for, and which rows it keeps.
const VIEWS = [
  { id: 'waiting', word: BRIDGE_WORDS.visitsWaiting, statuses: ['proposed', 'failed'], keep: () => true },
  { id: 'applied', word: BRIDGE_WORDS.visitsApplied, statuses: ['applied'], keep: (row) => (row.data || {}).change !== 'cancel' },
  { id: 'cancelled', word: BRIDGE_WORDS.visitsCancelled, statuses: ['applied'], keep: (row) => (row.data || {}).change === 'cancel' },
  { id: 'aside', word: BRIDGE_WORDS.visitsSetAside, statuses: ['rejected', 'superseded'], keep: () => true },
];

/* ---- loadViewRows(view) -- every status the view needs, merged, newest first ---- */
async function loadViewRows(view) {
  const allRows = [];

  for (const oneStatus of view.statuses) {
    const answer = await fetchVisits(oneStatus);

    if (!answer.ok) {
      return null;
    }

    // Phase 2b's paging answers { rows, total, page, pageSize }.
    const pageRows = answer.data && Array.isArray(answer.data.rows) ? answer.data.rows : [];

    for (const oneRow of pageRows) {
      allRows.push(oneRow);
    }
  }

  const keptRows = allRows.filter(view.keep);

  keptRows.sort((first, second) => String(second.receivedAt).localeCompare(String(first.receivedAt)));

  return keptRows;
}

/*****************************************************************
 *  SECTION 2 — THE SWITCH STRIP                                   *
 *****************************************************************/

// The words for the job-reveal rule (Dispatch's own switch).
const REVEAL_WORDS = {
  current_only: BRIDGE_WORDS.revealCurrentOnly,
  current_plus_count: BRIDGE_WORDS.revealCurrentPlusCount,
  full_day: BRIDGE_WORDS.revealFullDay,
};

/* ---- SwitchStrip({ settings }) -- how visits land, and where to change it ---- */
function SwitchStrip({ settings }) {
  if (!settings) {
    return null;
  }

  if (!settings.connected) {
    return (
      <div className="zb-notice zb-notice-warn" role="status">
        <BridgeIcon name="alert" />
        <span>{settings.problem || BRIDGE_WORDS.notConnected}</span>
      </div>
    );
  }

  const isAutomatic = settings.visitsMode === 'automatic';
  const modeWord = isAutomatic ? BRIDGE_WORDS.modeAutomatic : (settings.visitsMode === 'approve_each' ? BRIDGE_WORDS.modeApproveEach : BRIDGE_WORDS.modeUnknown);

  return (
    <div className="zb-notice zb-notice-info" role="status">
      <BridgeIcon name="calendar" />
      <div>
        <div className="zb-row zb-row-tight">
          <span>{BRIDGE_WORDS.modeLabel}:</span>
          <ToneChip tone={isAutomatic ? 'ok' : 'info'} icon={isAutomatic ? 'check' : 'usercheck'} word={modeWord} />
        </div>
        <div className="zb-row zb-row-tight">
          <span>{BRIDGE_WORDS.revealLabel}:</span>
          <strong>{REVEAL_WORDS[settings.revealMode] || BRIDGE_WORDS.modeUnknown}</strong>
        </div>
        <a href={settings.switchPage} target="_blank" rel="noopener noreferrer">{BRIDGE_WORDS.modeChangeIt}</a>
      </div>
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 — THE TAB                                            *
 *****************************************************************/

/* ---- VisitsTab({ onChanged }) -- onChanged() refreshes the page counts ---- */
export default function VisitsTab({ onChanged }) {
  const [viewId, setViewId] = useState('waiting');          // which view
  const [rows, setRows] = useState([]);                     // the cards
  const [loadState, setLoadState] = useState('loading');    // loading / ready / error
  const [settings, setSettings] = useState(null);           // Dispatch's switches
  const [selectedIds, setSelectedIds] = useState([]);       // ticked cards
  const [isBulkOpen, setIsBulkOpen] = useState(false);      // the shared why box
  const [isBulkBusy, setIsBulkBusy] = useState(false);
  const [bulkAnswer, setBulkAnswer] = useState('');         // what approve-many did

  const view = VIEWS.find((oneView) => oneView.id === viewId) || VIEWS[0];

  /* ---- loadRows(isQuiet) -- the cards for the current view ---- */
  const loadRows = useCallback(async (isQuiet) => {
    if (!isQuiet) {
      setLoadState('loading');
    }

    const viewRows = await loadViewRows(view);

    if (viewRows === null) {
      setLoadState('error');
      return;
    }

    setRows(viewRows);
    setSelectedIds([]);
    setLoadState('ready');
  }, [view]);

  useEffect(() => {
    loadRows(false);
  }, [loadRows]);

  useEffect(() => {
    fetchVisitSettings().then((answer) => {
      setSettings(answer.ok ? answer.data : { connected: false, problem: answer.message });
    });
  }, []);

  /* ---- afterDecision() -- reload the cards and the page counts ---- */
  function afterDecision() {
    loadRows(true);
    onChanged();
  }

  /* ---- toggleOne(id) -- tick / untick one card ---- */
  function toggleOne(recordId) {
    if (selectedIds.includes(recordId)) {
      setSelectedIds(selectedIds.filter((oneId) => oneId !== recordId));
    } else {
      setSelectedIds(selectedIds.concat(recordId));
    }
  }

  /* ---- approveTheTicked(whyText) -- approve-many, then say what happened ---- */
  async function approveTheTicked(whyText) {
    setIsBulkBusy(true);

    const answer = await approveManyRecords(selectedIds, whyText);

    setIsBulkBusy(false);
    setIsBulkOpen(false);

    if (!answer.ok) {
      setBulkAnswer(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    const counts = answer.data.counts;
    setBulkAnswer(counts.applied + ' put on the calendar, ' + counts.failed + ' need a person, ' + counts.skipped + ' skipped (already decided).');
    afterDecision();
  }

  return (
    <section aria-labelledby="zb-tab-visits-title">
      <h2 id="zb-tab-visits-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabVisits}</h2>
      <p className="zb-quiet">{BRIDGE_WORDS.visitsIntro}</p>

      <SwitchStrip settings={settings} />

      <div className="zb-filters">
        <label className="zb-field">
          <span className="zb-label">{BRIDGE_WORDS.visitsShow}</span>
          <select className="zb-select" value={viewId} onChange={(event) => setViewId(event.target.value)}>
            {VIEWS.map((oneView) => <option key={oneView.id} value={oneView.id}>{oneView.word}</option>)}
          </select>
        </label>
      </div>

      {viewId === 'waiting' && rows.length > 0 ? (
        <div className="zb-bulkbar">
          <button type="button" className="zb-button zb-button-plain" onClick={() => setSelectedIds(rows.map((oneRow) => oneRow.id))}>
            {BRIDGE_WORDS.selectAll}
          </button>
          <span className="zb-small">{selectedIds.length} {BRIDGE_WORDS.selectedCount}</span>
          <button type="button" className="zb-button zb-button-primary" disabled={selectedIds.length === 0} onClick={() => setIsBulkOpen(true)}>
            <BridgeIcon name="check" />
            <span>{BRIDGE_WORDS.approveSelected}</span>
          </button>
        </div>
      ) : null}

      {isBulkOpen ? (
        <WhyBox
          confirmWord={BRIDGE_WORDS.confirmVisit + ' (' + selectedIds.length + ')'}
          tone="primary"
          busy={isBulkBusy}
          onConfirm={approveTheTicked}
          onCancel={() => setIsBulkOpen(false)}
        />
      ) : null}

      {bulkAnswer ? <div className="zb-notice zb-notice-info" role="status"><span>{bulkAnswer}</span></div> : null}

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={() => loadRows(false)} /> : null}

      {loadState === 'ready' && rows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.visitsEmpty} /> : null}

      {loadState === 'ready' ? (
        <div className="zb-cards">
          {rows.map((oneRow) => (
            <VisitCard
              key={oneRow.id}
              record={oneRow}
              timeZone={settings ? settings.timeZone : null}
              isSelected={selectedIds.includes(oneRow.id)}
              onToggleSelect={toggleOne}
              onDecided={afterDecision}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
